'use client';
/**
 * Link component.
 * - has several variants
 * - automatically sends ProceedToPage analytics
 */
import { FC, MouseEvent, useCallback, ComponentProps, forwardRef } from 'react';
import classNames from 'classnames';
import NextLink from 'next/link'; // eslint-disable-line no-restricted-imports
import { trackEvent } from '@/analytics';
import { Button, getButtonClassName } from './Button';
import { isUriAbsolute } from '@/utils/url';
import { Icon } from '@/components/icon';
import openFilledIcon from '@/components/icon/svg/open-filled.svg';

type LinkProps = ComponentProps<typeof NextLink> & {
  variant?: keyof typeof variants | 'disabled-button';
  analyticsName?: string | null;
  noExternalIcon?: boolean; // matters only with external URIs
  newTab?: boolean; // by default, external URIs open in a new tab, internal URIs in the same tab
};

const variants = {
  bare: '',
  default: 'text-primary leading-none font-medium', // looks like obsolete
  'default-new': 'text-primary leading-none',
  /** Inline link that inherits surrounding text color/weight, just adds underline */
  inline: 'underline',
  'primary-button': getButtonClassName('primary'),
  'secondary-button': getButtonClassName('secondary'),
} as const;

export const Link: FC<LinkProps> = forwardRef(
  (
    {
      href,
      className,
      onClick,
      children,
      variant = 'default',
      analyticsName = '', //analyticsName,
      noExternalIcon = false,
      newTab,
      ...props
    },
    ref,
    // eslint-disable-next-line visual/complexity
  ) => {
    const targetUrl = getHrefString(href);
    const isAbsolute = isUriAbsolute(targetUrl);
    const isNewTab = newTab !== undefined ? newTab : isAbsolute;
    const tabName = isNewTab ? '_blank' : '';
    const handleClick = useOnClickWithAnalytics(targetUrl, analyticsName, onClick);
    const showIcon = isAbsolute && !noExternalIcon;

    if (!targetUrl)
      // https://onetakeda.atlassian.net/browse/MPRO-2633
      return null;

    if (variant === 'disabled-button') {
      return (
        <Button disabled={true} className="inline-block w-full sm:w-auto uppercase">
          {children}
        </Button>
      );
    }

    const variantClass = variants[variant];

    return (
      <NextLink
        {...props}
        className={classNames(variantClass, className)}
        href={targetUrl}
        onClick={handleClick}
        target={tabName}
        ref={ref}
      >
        {children}
        {showIcon && (
          <Icon
            width="18"
            height="18"
            src={openFilledIcon}
            className="ml-[5px] align-[-3px] inline"
          />
        )}
      </NextLink>
    );
  },
);

// See: https://nextjs.org/docs/app/guides/prefetching#disabled-prefetch
export const NoPrefetchLink: FC<LinkProps> = (props) => {
  return <Link {...props} prefetch={false} />;
};

Link.displayName = 'Link';

function useOnClickWithAnalytics(
  targetUrl: string,
  analyticsName: string | null,
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void,
) {
  return useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      const isAbsolute = isUriAbsolute(targetUrl);
      if (isAbsolute) {
        e.stopPropagation();
        if (analyticsName) {
          trackEvent('ProceedToExternalResource', {
            targetUrl: targetUrl,
            targetName: analyticsName,
          });
        }
      } else {
        if (targetUrl && analyticsName !== null) trackEvent('ProceedToPage', { targetUrl });
      }
      return onClick?.(e);
    },
    [targetUrl, onClick, analyticsName],
  );
}

function getHrefString(href: LinkProps['href']) {
  // Next.js can provide URL object instead of string for next/link
  return typeof href === 'string' ? href : href.href || '';
}
